import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getSocket, disconnectSocket } from '../lib/socket';

const AuthContext = createContext();

/** Purpose: expose the current authenticated workspace state and actions. Why: pages share one session/access source instead of maintaining conflicting local auth state. */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Purpose: own login, session restoration, bot/environment selection, and access checks. Why: centralizes identity and permissions for the entire frontend. */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [apiKey, setApiKey] = useState(localStorage.getItem('apiKey') || '');
  const [botAccess, setBotAccess] = useState([]);
  const [selectedBot, setSelectedBot] = useState(localStorage.getItem('selectedBot') || null);
  const [selectedEnv, setSelectedEnv] = useState(localStorage.getItem('selectedEnv') || 'dev');
  const [loading, setLoading] = useState(true); // Start with loading true to check session
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [agentPresence, setAgentPresence] = useState(() => {
    try { return JSON.parse(localStorage.getItem('agent_presence_preference')) || { status: 'online', customStatus: '' }; }
    catch { return { status: 'online', customStatus: '' }; }
  });

  // Logout function
  /** Purpose: Resolve logout in the auth context module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const logout = useCallback(() => {
    if (token && selectedBot) {
      axios.post('/api/v1/agents/presence', {
        botId: selectedBot,
        status: 'offline',
        isAvailable: false,
      }).catch(() => {});
    }
    setUser(null);
    setToken('');
    setBotAccess([]);
    setSelectedBot(null);
    setSelectedEnv('dev');
    setLastActivity(Date.now());
    localStorage.removeItem('token');
    localStorage.removeItem('apiKey');
    localStorage.removeItem('selectedBot');
    localStorage.removeItem('selectedEnv');
    localStorage.removeItem('lastActivity');
    delete axios.defaults.headers.common['Authorization'];
    disconnectSocket();
  }, [selectedBot, token, user?.role]);

  // Update axios headers whenever token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
      // Store last activity timestamp
      localStorage.setItem('lastActivity', Date.now().toString());
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
      localStorage.removeItem('lastActivity');
    }
  }, [token]);

  useEffect(() => {
    /** Purpose: Find the selected bot's access entry. Why: Role and environment checks must follow the current bot. */
    const accessEntry = botAccess.find((entry) => entry.botId === selectedBot);
    const assignedRoles = Array.isArray(accessEntry?.workspaceRoles) && accessEntry.workspaceRoles.length > 0
      ? accessEntry.workspaceRoles
      : (accessEntry?.workspaceRole ? [accessEntry.workspaceRole] : []);
    // Only explicit agent-desk roles in this bot's Access Control should
    // create presence or join agent rooms. Platform admins are not agents.
    /** Purpose: Provide the is agent role operation for the auth context module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
    const isAgentRole = assignedRoles.some((r) => ['agent_admin', 'supervisor', 'agent'].includes(r));
    if (!user || !token || !selectedBot || !isAgentRole) {
      return undefined;
    }

    const socket = getSocket();
    const agentId = user.id || user.userId || user._id;

    if (agentId) {
      socket.emit('agent:join', { agentId, botId: selectedBot, envName: selectedEnv || 'dev' });
    }

    axios.post('/api/v1/agents/presence', {
      botId: selectedBot,
      status: agentPresence.status || 'online',
      customStatus: agentPresence.customStatus || '',
      isAvailable: agentPresence.status === 'online',
      socketId: socket?.id || '',
    }).catch(() => {});

    return () => {
      if (agentId) {
        socket.emit('agent:leave', { agentId });
      }
    };
  }, [agentPresence, botAccess, selectedBot, selectedEnv, token, user]);

  /** Purpose: Update local presence preferences and synchronize the agent status. Why: Inbox availability and visible status must stay consistent across reloads and sessions. */
  const updateAgentPresence = useCallback(async ({ status = 'online', customStatus = '' } = {}) => {
    const next = { status, customStatus: status === 'custom' ? String(customStatus || '').trim() : '' };
    setAgentPresence(next);
    localStorage.setItem('agent_presence_preference', JSON.stringify(next));
    if (!selectedBot) return null;
    const response = await axios.post('/api/v1/agents/presence', {
      botId: selectedBot,
      ...next,
      isAvailable: status === 'online',
    });
    const returnedAgent = response.data?.agent || null;
    if (returnedAgent) {
      const confirmed = {
        // `busy` with manualUnavailable=false is an automatic capacity state;
        // keep the user's preference as Available so releasing a chat can make
        // the agent routable again. Explicit Busy/Away/Break remains persisted.
        status: returnedAgent.status === 'busy' && returnedAgent.manualUnavailable === false
          ? 'online'
          : (returnedAgent.status || next.status),
        customStatus: returnedAgent.customStatus || '',
      };
      setAgentPresence(confirmed);
      localStorage.setItem('agent_presence_preference', JSON.stringify(confirmed));
    }
    return returnedAgent;
  }, [selectedBot]);

  // Request deduplication - prevent multiple simultaneous requests
  const fetchUserInfoPromise = useRef(null);

  // Fetch user info
  /** Purpose: Load fetch user info for the auth context module. Why: The selected view or service needs current scoped data before continuing. */
  const fetchUserInfo = useCallback(async () => {
    // If a request is already in progress, return the existing promise
    if (fetchUserInfoPromise.current) {
      return fetchUserInfoPromise.current;
    }

    // Create new request promise
    fetchUserInfoPromise.current = (async () => {
      try {
        const response = await axios.get('/api/v1/auth/me');
        setUser(response.data.user);
        setBotAccess(response.data.botAccess || []);
        
        // Restore selected bot if it exists in access
        const savedBot = localStorage.getItem('selectedBot');
        if (savedBot && response.data.botAccess?.some(a => a.botId === savedBot)) {
          setSelectedBot(savedBot);
        } else if (response.data.botAccess?.length > 0) {
          // Auto-select first bot if no selection
          setSelectedBot(response.data.botAccess[0].botId);
          localStorage.setItem('selectedBot', response.data.botAccess[0].botId);
        }
        
        // Update last activity
        setLastActivity(Date.now());
        localStorage.setItem('lastActivity', Date.now().toString());
      } catch (error) {
        console.error('Error fetching user info:', error);
        // Token might be invalid, clear it
        if (error.response?.status === 401) {
          logout();
        } else if (error.response?.status === 429) {
          // Rate limited - wait a bit and retry once
          console.warn('Rate limited, waiting before retry...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          // Clear the promise so we can retry
          fetchUserInfoPromise.current = null;
        }
        throw error;
      } finally {
        // Clear the promise after request completes
        fetchUserInfoPromise.current = null;
      }
    })();

    return fetchUserInfoPromise.current;
  }, [logout]);

  // Restore session on mount (only once)
  const hasRestoredSession = useRef(false);
  const restoreSessionPromise = useRef(null);
  
  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasRestoredSession.current) {
      setLoading(false);
      return;
    }
    
    // If restore is already in progress, don't start another
    if (restoreSessionPromise.current) {
      return;
    }
    
    hasRestoredSession.current = true;

    // Purpose: restore a still-valid saved session on app startup. Why: prevents forcing a new login on every refresh while enforcing the idle timeout.
    const restoreSession = async () => {
      try {
        const savedToken = localStorage.getItem('token');
        const savedLastActivity = localStorage.getItem('lastActivity');
        
        if (!savedToken) {
          // No token saved, user needs to login
          setLoading(false);
          return;
        }
        
        // Check if session expired (2 hours = 7200000 ms)
        const IDLE_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
        const timeSinceLastActivity = Date.now() - parseInt(savedLastActivity || 0);
        
        if (timeSinceLastActivity > IDLE_TIMEOUT) {
          // Session expired due to idle time
          console.log('Session expired due to idle timeout');
          logout();
          setLoading(false);
          return;
        }
        
        // Set token and fetch user info (with deduplication)
        setToken(savedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        
        restoreSessionPromise.current = fetchUserInfo();
        await restoreSessionPromise.current;
        
        // Successfully restored session
        console.log('Session restored successfully');
      } catch (error) {
        // Error already handled in fetchUserInfo
        if (error.response?.status === 401) {
          // Token is invalid, clear it
          console.log('Token invalid, clearing session');
          logout();
        } else if (error.response?.status !== 429) {
          // Only clear session if not rate limited
          console.error('Failed to restore session:', error);
          // Don't logout on network errors, just set loading to false
          // User can still use the app if token is valid
        }
      } finally {
        restoreSessionPromise.current = null;
        setLoading(false);
      }
    };

    restoreSession();
  }, [logout]); // Include logout in deps

  // Track user activity to reset idle timer
  useEffect(() => {
    if (!token) return;

    // Purpose: persist the latest user interaction time. Why: the idle-session check uses this timestamp to expire abandoned sessions.
    const updateActivity = () => {
      setLastActivity(Date.now());
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Track mouse movement, clicks, keyboard, scroll
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Check idle timeout every minute
    /** Purpose: Resolve idle check interval in the auth context module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
    const idleCheckInterval = setInterval(() => {
      const savedLastActivity = localStorage.getItem('lastActivity');
      if (savedLastActivity) {
        const IDLE_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
        const timeSinceLastActivity = Date.now() - parseInt(savedLastActivity);
        
        if (timeSinceLastActivity > IDLE_TIMEOUT) {
          console.log('Session expired due to idle timeout');
          logout();
          window.location.href = '/login';
        }
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(idleCheckInterval);
    };
  }, [token, logout]);

  /** Purpose: authenticate credentials and initialize the returned session. Why: one action updates user, token, bot access, and the default bot consistently. */
  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post('/api/v1/auth/login', { email, password });
      const { user, token, botAccess, requiresVerification } = response.data;
      
      setUser(user);
      if (token) {
        // Set token immediately in axios headers (before state update)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('token', token);
        setToken(token);
        setLastActivity(Date.now());
        localStorage.setItem('lastActivity', Date.now().toString());
      }
      setBotAccess(botAccess || []);
      
      // Auto-select first bot if available and verified
      if (botAccess && botAccess.length > 0 && !requiresVerification) {
        setSelectedBot(botAccess[0].botId);
        localStorage.setItem('selectedBot', botAccess[0].botId);
      }
      
      setLoading(false);
      return { success: true, user, botAccess, requiresVerification, ...response.data };
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  // Used after email verification: the verification endpoint already returns
  // a complete authenticated session, so the user must not be sent back to
  // the login screen or forced to enter their password again.
  /** Purpose: accept the authenticated session returned by email verification. Why: verified users can continue without re-entering credentials. */
  const completeVerification = (session) => {
    const verifiedBotAccess = session.botAccess || [];

    setUser(session.user);
    setBotAccess(verifiedBotAccess);

    if (session.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${session.token}`;
      localStorage.setItem('token', session.token);
      setToken(session.token);
      setLastActivity(Date.now());
      localStorage.setItem('lastActivity', Date.now().toString());
    }

    if (verifiedBotAccess.length > 0) {
      setSelectedBot(verifiedBotAccess[0].botId);
      localStorage.setItem('selectedBot', verifiedBotAccess[0].botId);
    }
  };

  /** Purpose: create an account and initialize its session/access state. Why: registration follows the same session setup contract as login. */
  const register = async (name, email, password, selectedPlan = '') => {
    setLoading(true);
    try {
      const response = await axios.post('/api/v1/auth/register', { name, email, password, selectedPlan });
      const { user, token, botAccess, requiresVerification } = response.data;
      
      setUser(user);
      if (token) {
        // Set token immediately in axios headers (before state update)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('token', token);
        setToken(token);
        setLastActivity(Date.now());
        localStorage.setItem('lastActivity', Date.now().toString());
      }
      setBotAccess(botAccess || []);
      
      // Auto-select first bot if available and verified
      if (botAccess && botAccess.length > 0 && !requiresVerification) {
        setSelectedBot(botAccess[0].botId);
        localStorage.setItem('selectedBot', botAccess[0].botId);
      }
      
      setLoading(false);
      return { success: true, user, botAccess, requiresVerification, ...response.data };
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  /** Purpose: select and persist the active bot. Why: all bot-scoped pages and requests must operate against the same workspace. */
  const selectBot = (botId) => {
    setSelectedBot(botId);
    localStorage.setItem('selectedBot', botId);
  };

  /** Purpose: select and persist the active environment. Why: API calls and flow editing must consistently target DEV or PROD. */
  const selectEnv = (envName) => {
    setSelectedEnv(envName);
    localStorage.setItem('selectedEnv', envName);
  };

  /** Purpose: normalize and persist the optional workspace API key. Why: consumers need one auth-context source that stays in sync with browser storage. */
  const saveApiKey = (nextApiKey) => {
    const normalizedApiKey = (nextApiKey || '').trim();
    setApiKey(normalizedApiKey);

    if (normalizedApiKey) {
      localStorage.setItem('apiKey', normalizedApiKey);
    } else {
      localStorage.removeItem('apiKey');
    }
  };

  // Get available environments for selected bot
  /** Purpose: return environments granted for the selected bot. Why: environment controls must reflect Access Control rather than assume both are available. */
  const getAvailableEnvs = () => {
    if (!selectedBot || !botAccess) return [];
    /** Purpose: Find the selected bot's permissions before calculating access. Why: Environment availability depends on bot-specific membership. */
    const access = botAccess.find(a => a.botId === selectedBot);
    return access ? access.environmentsAllowed : [];
  };

  // Services enabled for currently selected bot's tenant
  /** Purpose: return services enabled for the selected bot's tenant. Why: navigation and feature access depend on the tenant's configured products. */
  const getEnabledServices = () => {
    if (isAdmin()) return ['voice', 'chat', 'whatsapp'];
    if (!selectedBot || !botAccess) return [];
    /** Purpose: Resolve the selected bot's access when computing environment choices. Why: The UI must not show scopes granted only on another bot. */
    const access = botAccess.find((a) => a.botId === selectedBot);
    return Array.isArray(access?.enabledServices) ? access.enabledServices : [];
  };

  // Check if user is admin
  /** Purpose: identify platform-level administrators. Why: platform-wide privileges must remain distinct from bot agent roles. */
  const isAdmin = () => {
    return user?.isSuperAdmin === true;
  };

  // Platform admins manage every bot; bot admins manage only assigned bots.
  // When a user holds admin (e.g. admin + dev), admin access must be granted everywhere.
  /** Purpose: check administrator rights for a specific bot. Why: scoped admin checks prevent one bot's membership from granting rights to another. */
  const isBotAdmin = (botId = selectedBot) => {
    if (isAdmin()) return true;
    /** Purpose: Find permissions for the requested bot ID. Why: Access checks must not rely on a stale selected-bot value. */
    const access = botAccess.find((entry) => entry.botId === botId);
    if (!access) return false;
    const roles = Array.isArray(access.workspaceRoles) && access.workspaceRoles.length > 0
      ? access.workspaceRoles
      : (access.workspaceRole ? [access.workspaceRole] : []);
    return access.accessLevel === 'admin' || roles.includes('admin') || access.workspaceRole === 'admin';
  };

  // Check if user has access to current bot
  /** Purpose: check whether the current user can enter a bot workspace. Why: routes and bot selectors need to guard bot-scoped data. */
  const hasBotAccess = (botId) => {
    if (isAdmin()) return true;
    return botAccess.some(a => a.botId === botId);
  };

  // Check if user has access to current environment
  /** Purpose: check whether a user can access a bot environment. Why: UI actions must honor DEV/PROD grants instead of relying only on a selected environment. */
  const hasEnvAccess = (botId, envName) => {
    if (isAdmin()) return true;
    /** Purpose: Resolve the bot entry used by the caller's access check. Why: The result keeps role gating aligned with bot membership. */
    const access = botAccess.find(a => a.botId === botId);
    return access ? access.environmentsAllowed.includes(envName) : false;
  };

  /** Purpose: check access to a named product service. Why: pages can hide or protect features not enabled for the selected tenant. */
  const hasServiceAccess = (service) => {
    if (isAdmin()) return true;
    return getEnabledServices().includes(service);
  };

  const selectedBotDetails = selectedBot
    ? botAccess.find((entry) => entry.botId === selectedBot) || null
    : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        apiKey,
        botAccess,
        selectedBotDetails,
        selectedBot,
        selectedEnv,
        loading,
        login,
        register,
        completeVerification,
        logout,
        selectBot,
        selectEnv,
        saveApiKey,
        getAvailableEnvs,
        getEnabledServices,
        isAdmin,
        isBotAdmin,
        hasBotAccess,
        hasEnvAccess,
        hasServiceAccess,
        fetchUserInfo,
        agentPresence,
        updateAgentPresence,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
